import { createSelector, createSlice } from "@reduxjs/toolkit"
import { RootState } from "integrations/redux"
import {
    CreatableCurrencyPolicy,
    CurrencyPolicy,
    CurrencyPolicyMetric,
    CurrencyPolicyRecipient,
    CurrencyPolicyRecipientInput,
    CurrencyPolicyUser,
} from "@nova/schemas"

export type CurrencyPolicyFormState = {
    name: string
    userIds: string[]
    droneTypes: string[]
    metric: CurrencyPolicyMetric
    threshold: number
    windowDays: number
    warningDays: number
    emailReminder: boolean
    notifyAllUsers: boolean
    recipients: CurrencyPolicyRecipientInput[]
}

type CurrencyPolicyFormSliceState = {
    form: CurrencyPolicyFormState
    submitting: boolean
}

const initialState: CurrencyPolicyFormSliceState = {
    form: {
        name: "",
        userIds: [],
        droneTypes: [],
        metric: "flights",
        threshold: 2,
        windowDays: 60,
        warningDays: 14,
        emailReminder: true,
        notifyAllUsers: false,
        recipients: [],
    },
    submitting: false,
}

export const currencyPolicyFormSliceName = "currencyPolicyForm" as const

export const {
    actions: { updateForm, resetForm, loadPolicy, setSubmitting },
    reducer: currencyPolicyFormReducer,
} = createSlice({
    name: currencyPolicyFormSliceName,
    initialState,
    reducers: {
        updateForm: <K extends keyof CurrencyPolicyFormState>(
            state: CurrencyPolicyFormSliceState,
            action: { payload: { key: K; value: CurrencyPolicyFormState[K] } }
        ) => {
            state.form[action.payload.key] = action.payload.value
        },
        resetForm: () => initialState,
        loadPolicy: (
            _state: CurrencyPolicyFormSliceState,
            action: {
                payload: {
                    policy: CurrencyPolicy
                    users: CurrencyPolicyUser[]
                    recipients: CurrencyPolicyRecipient[]
                }
            }
        ) => {
            const { policy, users, recipients } = action.payload
            return {
                form: {
                    name: policy.name,
                    userIds: users.map((u) => u.userId),
                    droneTypes: policy.droneTypes,
                    metric: policy.metric,
                    threshold: policy.threshold,
                    windowDays: policy.windowDays,
                    warningDays: policy.warningDays,
                    emailReminder: policy.emailReminder,
                    notifyAllUsers: policy.notifyAllUsers,
                    recipients: recipients.map((r) =>
                        r.userId ? { user_id: r.userId } : { email: r.email ?? "" }
                    ),
                },
                submitting: false,
            }
        },
        setSubmitting: (state, action: { payload: CurrencyPolicyFormSliceState["submitting"] }) => {
            state.submitting = action.payload
        },
    },
})

const root = (state: RootState) => state[currencyPolicyFormSliceName]

export const selectForm = createSelector([root], (s) => s.form)

export const selectFormAsCreatable = createSelector(
    [selectForm],
    (form): CreatableCurrencyPolicy => ({
        name: form.name,
        metric: form.metric,
        threshold: form.threshold,
        window_days: form.windowDays,
        warning_days: form.warningDays,
        drone_types: form.droneTypes,
        email_reminder: form.emailReminder,
        notify_all_users: form.notifyAllUsers,
        user_ids: form.userIds,
        recipients: form.recipients,
    })
)
export const selectSubmitting = createSelector([root], (s) => s.submitting)
